import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import hiveService from '../services/hive';
import { postsApi, workspaceApi, userApi } from '../services/api';
import { buildPostPath } from '../utils/postUrls';
import MarkdownRenderer from '../components/MarkdownRenderer/MarkdownRenderer';
import TagInput from '../components/TagInput/TagInput';
import CommunitySelect from '../components/CommunitySelect/CommunitySelect';
import BeneficiariesModal from '../components/BeneficiariesModal/BeneficiariesModal';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import RichEditor from '../components/RichEditor';
import { buildArticleMetadataForTargetApp, getAvailableArticleTargetApps } from '../config/articleTargetApps';
import { htmlToMarkdown, shortcodesToMarkdown } from '../components/RichEditor/richEditorUtils';
import useBodyScrollLock from '../utils/useBodyScrollLock';
import './CreateArticle.css';
import '../components/CreatePostModal/CreatePostModal.css';

const CreateArticle = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated } = useAuth();
  const { defaultBeneficiaries } = useSettings();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [community, setCommunity] = useState('');
  const [tags, setTags] = useState([]); // array of tag strings
  const [communities, setCommunities] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [rewardOption, setRewardOption] = useState('50'); // default 50% HBD / 50% HP
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [beneficiariesTags, setBeneficiariesTags] = useState([]); // strings like "user=weight"
  const [beneficiariesInitialized, setBeneficiariesInitialized] = useState(false);
  const [showBeneficiariesModal, setShowBeneficiariesModal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!beneficiariesInitialized && defaultBeneficiaries && defaultBeneficiaries.length > 0) {
      setBeneficiariesTags(defaultBeneficiaries);
      setBeneficiariesInitialized(true);
    }
  }, [defaultBeneficiaries, beneficiariesInitialized]);

  const richEditorRef = useRef(null);
  const location = useLocation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isDistractionlessMode, setIsDistractionlessMode] = useState(false);
  const [coverImage, setCoverImage] = useState('');
  const [customCover, setCustomCover] = useState('');
  const [customCoverStatus, setCustomCoverStatus] = useState('idle');
  const [includeInBlog, setIncludeInBlog] = useState(false);
  const [bodyFormat, setBodyFormat] = useState('shortcode'); // 'markdown' | 'shortcode'
  const [detectedImages, setDetectedImages] = useState([]);

  // Draft autosave state
  const [draftId, setDraftId] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const draftIdRef = useRef(null);
  const articleSessionId = useRef(crypto.randomUUID()); // stable per mount; new visit = new UUID
  const debounceRef = useRef(null);
  const postingRef = useRef(false);
  const currentStateRef = useRef({});
  const isDirtyRef = useRef(false); // true once user has edited after load
  const isSavingRef = useRef(false); // prevents concurrent createDraft calls
  const pendingSaveRef = useRef(false); // a save was requested while one was in-flight
  // Start as true when loading from a draft/template/edit so the editor isn't mounted until content is ready
  const [loadingWorkspace, setLoadingWorkspace] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return !!(p.get('draft') || p.get('template') || (p.get('edit') === 'true' && p.get('author') && p.get('permlink')));
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editOriginalPermlink, setEditOriginalPermlink] = useState('');
  const [editOriginalParentPermlink, setEditOriginalParentPermlink] = useState('');
  const [editWindowExpired, setEditWindowExpired] = useState(false);
  const [lockedFirstTag, setLockedFirstTag] = useState(null);
  const [editCommunityTitle, setEditCommunityTitle] = useState('');
  const [urlCommunityTitle, setUrlCommunityTitle] = useState('');

  // Schedule modal state
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [schedulePostingKey, setSchedulePostingKey] = useState('');
  const [scheduleKeyStatus, setScheduleKeyStatus] = useState('idle'); // 'idle'|'verifying'|'valid'|'invalid'
  const [scheduling, setScheduling] = useState(false);
  const [storedKeyStatus, setStoredKeyStatus] = useState(null); // null|'checking'|'has-key'|'no-key'
  const [showKeyOverride, setShowKeyOverride] = useState(false); // use a different key than stored
  const [storePostingKey, setStorePostingKey] = useState(false); // checkbox: remember this key
  const [showScheduleKeyAdvanced, setShowScheduleKeyAdvanced] = useState(false); // advanced collapsible
  const [savingTemplate, setSavingTemplate] = useState(false);
  const availableTargetApps = useMemo(() => getAvailableArticleTargetApps(), []);
  const targetAppOptions = useMemo(
    () => availableTargetApps.filter((app) => typeof app?.jsonMetadata?.app === 'string' && app.jsonMetadata.app.trim() !== ''),
    [availableTargetApps]
  );
  const [targetApp, setTargetApp] = useState(() => (targetAppOptions[0]?.key || ''));

  useEffect(() => {
    if (targetAppOptions.length === 0) {
      if (targetApp !== '') {
        setTargetApp('');
      }
      return;
    }

    const hasSelectedOption = targetAppOptions.some((app) => app.key === targetApp);
    if (!hasSelectedOption) {
      setTargetApp(targetAppOptions[0].key);
    }
  }, [targetAppOptions, targetApp]);

  // detect image URLs whenever body changes
  useEffect(() => {
    const imgs = Array.from(new Set(hiveService.extractImages(body || '')));
    setDetectedImages(imgs);
    // if coverImage is unset and there are images, pick first
    if (!coverImage && imgs.length) {
      setCoverImage(imgs[0]);
    }
  }, [body, coverImage]);

  useEffect(() => {
    if (coverImage !== '__custom') {
      setCustomCoverStatus('idle');
      return;
    }

    const url = customCover.trim();
    if (!url) {
      setCustomCoverStatus('idle');
      return;
    }

    let cancelled = false;
    const image = new Image();

    setCustomCoverStatus('loading');

    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        setCustomCoverStatus('invalid');
      }
    }, 10000);

    image.onload = () => {
      if (cancelled) return;
      window.clearTimeout(timeoutId);
      setCustomCoverStatus('valid');
    };

    image.onerror = () => {
      if (cancelled) return;
      window.clearTimeout(timeoutId);
      setCustomCoverStatus('invalid');
    };

    image.src = url;

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
    };
  }, [coverImage, customCover]);

  // Prefill community from ?community= when present (e.g. clicked from a community page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('community');
    if (!q) return;
    setCommunity(q);
    setUrlCommunityTitle('');
    hiveService.getCommunityTitle(q)
      .then(title => { if (title && title !== q) setUrlCommunityTitle(title); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Load ?draft=<id> or ?template=<id> on mount (one-time)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const draftParam = params.get('draft');
    const tplParam = params.get('template');
    if (!draftParam && !tplParam) return;

    const username = user?.username || localStorage.getItem('hive_username');
    setLoadingWorkspace(true);

    const loadAndPopulate = (data) => {
      if (!data) return;
      if (data.title) setTitle(data.title);
      if (data.body_html) setBodyHtml(data.body_html);
      if (data.body) setBody(data.body);
      if (data.community) setCommunity(data.community);
      if (Array.isArray(data.tags)) setTags(data.tags);
      if (data.target_app) setTargetApp(data.target_app);
      if (data.body_format) setBodyFormat(data.body_format);
      if (data.reward_option) setRewardOption(data.reward_option);
      if (Array.isArray(data.beneficiaries)) setBeneficiariesTags(data.beneficiaries);
      if (data.cover_image) setCoverImage(data.cover_image);
      if (data.include_in_blog != null) setIncludeInBlog(data.include_in_blog);
    };

    if (draftParam) {
      workspaceApi.getDraft(draftParam, username)
        .then(res => {
          if (res?.draft) {
            loadAndPopulate(res.draft);
            setDraftId(Number(draftParam));
            draftIdRef.current = Number(draftParam);
          } else {
            setError('Draft not found or does not belong to you.');
            navigate('/create-article', { replace: true });
          }
        })
        .catch(err => {
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            setError('Draft not found or does not belong to you.');
          } else {
            setError('Failed to load draft. Please try again.');
          }
          navigate('/create-article', { replace: true });
        })
        .finally(() => setLoadingWorkspace(false));
    } else {
      workspaceApi.getTemplate(tplParam, username)
        .then(res => {
          if (res?.template) {
            loadAndPopulate(res.template);
            // draftId stays null — using a template starts a brand-new draft; new UUID already set
          } else {
            setError('Template not found or does not belong to you.');
            navigate('/create-article', { replace: true });
          }
        })
        .catch(err => {
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            setError('Template not found or does not belong to you.');
          } else {
            setError('Failed to load template. Please try again.');
          }
          navigate('/create-article', { replace: true });
        })
        .finally(() => setLoadingWorkspace(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load ?edit=true&author=<author>&permlink=<permlink> on mount (one-time)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('edit') !== 'true' || !params.get('author') || !params.get('permlink')) return;

    const authorParam = params.get('author');
    const permlinkParam = params.get('permlink');
    const username = user?.username || localStorage.getItem('hive_username');

    setIsEditMode(true);
    setEditOriginalPermlink(permlinkParam);
    setLoadingWorkspace(true);

    const fetchPost = async () => {
      try {
        const post = await hiveService.client.database.call('get_content', [authorParam, permlinkParam]);
        if (!post || !post.author) throw new Error('Post not found');
        if (post.author.toLowerCase() !== username?.toLowerCase()) {
          setError('You are not the author of this post');
          setIsEditMode(false);
          setLoadingWorkspace(false);
          return;
        }

        const createdAtMs = post?.created ? new Date(post.created).getTime() : null;
        const isWithinEditWindow = Number.isFinite(createdAtMs)
          ? (Date.now() - createdAtMs) < (7 * 24 * 60 * 60 * 1000)
          : false;
        if (!isWithinEditWindow) {
          setEditWindowExpired(true);
          setError('This article can no longer be edited after 7 days.');
        }

        setTitle(post.title || '');
        const pp = post.parent_permlink || '';
        setCommunity(pp);
        setEditOriginalParentPermlink(pp);

        // Resolve community title: try loaded list first, then blockchain
        const loadedCommunities = communities;
        const found = loadedCommunities.find(c => c.name === pp);
        if (found?.title) {
          setEditCommunityTitle(found.title);
        } else if (pp) {
          hiveService.getCommunityTitle(pp)
            .then(title => setEditCommunityTitle(title || pp))
            .catch(() => setEditCommunityTitle(pp));
        }

        let meta = {};
        try { meta = JSON.parse(post.json_metadata); } catch {}
        const allTags = meta.tags || [];
        const tagsWithoutCommunity = allTags.filter(t => t !== pp);
        if (tagsWithoutCommunity.length > 0) {
          setLockedFirstTag(tagsWithoutCommunity[0]);
          setTags(tagsWithoutCommunity.slice(1));
        } else {
          setLockedFirstTag(null);
          setTags([]);
        }

        const rawBody = post.body || '';
        // Pass the raw body directly — RichEditor's normalizeInputContent correctly detects
        // whether it is HTML or markdown and converts it in one clean pass, avoiding the
        // double-escaping of & in image URLs that happens when we pre-convert to HTML here.
        setBodyHtml(rawBody);
        setBody(rawBody);

        if (meta.shortcode) setBodyFormat('shortcode');
        else setBodyFormat('markdown');

        const originalApp = typeof meta.app === 'string' ? meta.app : '';
        if (originalApp) {
          const matchedTarget = targetAppOptions.find(opt => opt?.jsonMetadata?.app === originalApp);
          if (matchedTarget?.key) {
            setTargetApp(matchedTarget.key);
          }
        }

        const img = meta.featured_image
          || (Array.isArray(meta.image) ? meta.image[0] : '')
          || (Array.isArray(meta.images) ? meta.images[0] : '')
          || '';
        if (img) setCoverImage(img);
      } catch (err) {
        console.error('Failed to load post for edit:', err);
        setError('Failed to load post: ' + (err.message || 'Unknown error'));
      } finally {
        setLoadingWorkspace(false);
      }
    };

    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep currentStateRef in sync so autosave callback always has the latest values
  useEffect(() => {
    currentStateRef.current = {
      title, body, bodyHtml, community, tags, targetApp, bodyFormat,
      rewardOption, beneficiariesTags, coverImage, customCover, includeInBlog,
    };
  }, [title, body, bodyHtml, community, tags, targetApp, bodyFormat,
      rewardOption, beneficiariesTags, coverImage, customCover, includeInBlog]);

  // Autosave: 3-second debounce after any field change (only once user has made an edit)
  useEffect(() => {
    if (isEditMode) return;
    if (!title.trim() || postingRef.current || loadingWorkspace) return;
    if (!isDirtyRef.current) return; // don't autosave pristine template/draft load
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { saveCurrentState(); }, 3000);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, body, bodyHtml, community, tags, targetApp, bodyFormat,
      rewardOption, beneficiariesTags, coverImage, customCover, includeInBlog,
      isEditMode]);

  // Autosave: 60-second interval fallback
  useEffect(() => {
    const interval = setInterval(() => {
      if (isEditMode) return;
      if (!currentStateRef.current?.title?.trim() || postingRef.current) return;
      saveCurrentState();
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // Validate prefilled community after communities load — but never clear one that
  // was explicitly provided via ?community= (the user may not follow that community)
  useEffect(() => {
    if (isEditMode) return;
    if (!community || communities.length === 0) return;
    const fromUrl = new URLSearchParams(location.search).get('community');
    if (community === fromUrl) return; // URL-supplied community is always trusted
    // Match against both the stored name and the hive- prefixed/stripped variant,
    // since the communities list may use either form depending on which sync path
    // populated the DB (some paths store "194913", others store "hive-194913").
    const communityStripped = community.replace(/^hive-/, '');
    const found = communities.find(c =>
      c.name === community ||
      c.name === communityStripped ||
      c.name === `hive-${communityStripped}`
    );
    if (!found) {
      setCommunity('');
    }
  }, [isEditMode, community, communities, location.search]);

  // Refresh subscriptions once on mount, then reload the community list so
  // newly-synced memberships (e.g. communities subscribed to on-chain but not
  // yet in the local DB) are immediately visible in the dropdown.
  useEffect(() => {
    const refreshSubscriptions = async () => {
      const uname = user?.username || localStorage.getItem('hive_username');
      if (uname) {
        try {
          await fetch(`/api/user/${encodeURIComponent(uname)}/subscriptions/refresh?force=true`, {
            method: 'POST'
          });
          // Re-fetch the community list now that memberships are up to date.
          // loadCommunities is defined below via useCallback — capture it by ref
          // so this effect doesn't depend on it and cause an infinite loop.
          loadCommunitiesRef.current?.();
        } catch (e) {
          console.warn('Failed to refresh subscriptions:', e.message);
        }
      }
    };
    refreshSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  // Stable ref so the subscription-refresh effect can call loadCommunities
  // without creating a circular dependency.
  const loadCommunitiesRef = useRef(null);

  const loadCommunities = useCallback(async () => {
    try {
      setCommunitiesLoading(true);
      const uname = user?.username || localStorage.getItem('hive_username');
      let url = '/api/feed/communities?sort=rank&limit=50';
      if (uname) {
        url = `/api/feed/communities/mine?username=${encodeURIComponent(uname)}&limit=1000`;
      }
      const res = await fetch(url);
      const data = await res.json();
      setCommunities(data.communities || []);
    } catch (err) {
      console.error('Failed to load communities', err);
    } finally {
      setCommunitiesLoading(false);
    }
  }, [user?.username]);

  // Keep the ref in sync so the refresh effect always calls the latest version.
  useEffect(() => { loadCommunitiesRef.current = loadCommunities; }, [loadCommunities]);

  // Load communities on mount
  useEffect(() => { loadCommunities(); }, [loadCommunities]);

  // ── Autosave helper ────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveCurrentState = useCallback(async () => {
    if (isEditMode) return;
    const s = currentStateRef.current;
    if (!s.title?.trim()) return;
    const username = user?.username || localStorage.getItem('hive_username');
    if (!username) return;

    // Prevent concurrent saves — if one is already running, flag it and return.
    // The in-flight save will retry with the latest state once it finishes.
    if (isSavingRef.current) {
      pendingSaveRef.current = true;
      return;
    }
    isSavingRef.current = true;
    setAutosaveStatus('saving');
    try {
      const payload = {
        author: username,
        title: s.title,
        body: s.body,
        body_html: s.bodyHtml,
        community: s.community,
        tags: s.tags,
        target_app: s.targetApp,
        body_format: s.bodyFormat,
        reward_option: s.rewardOption,
        beneficiaries: s.beneficiariesTags,
        cover_image: s.coverImage === '__custom' ? (s.customCover?.trim() || null) : (s.coverImage || null),
        include_in_blog: s.includeInBlog,
        session_key: articleSessionId.current,
      };
      if (draftIdRef.current) {
        await workspaceApi.updateDraft(draftIdRef.current, payload);
      } else {
        const result = await workspaceApi.createDraft(payload);
        if (result?.id) {
          draftIdRef.current = result.id;
          setDraftId(result.id);
        }
      }
      setAutosaveStatus('saved');
      setTimeout(() => setAutosaveStatus(prev => prev === 'saved' ? 'idle' : prev), 3000);
    } catch {
      setAutosaveStatus('error');
      setTimeout(() => setAutosaveStatus(prev => prev === 'error' ? 'idle' : prev), 5000);
    } finally {
      isSavingRef.current = false;
      // If another save was requested while this one was running, replay it now
      // so the latest editor state is persisted.
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        saveCurrentState();
      }
    }
  }, [user?.username, isEditMode]);

  // ── Schedule posting key verification ──────────────────────────────────────
  const handleVerifyScheduleKey = async () => {
    const username = user?.username || localStorage.getItem('hive_username');
    if (!schedulePostingKey.trim()) return;
    setScheduleKeyStatus('verifying');
    try {
      const valid = await hiveService.verifyPostingKey(username, schedulePostingKey.trim());
      setScheduleKeyStatus(valid ? 'valid' : 'invalid');
    } catch {
      setScheduleKeyStatus('invalid');
    }
  };

  // ── Save as Template ────────────────────────────────────────────────────────
  const handleSaveAsTemplate = async () => {
    if (!title.trim()) {
      setError('Please add a title before saving as a template.');
      return;
    }
    const username = user?.username || localStorage.getItem('hive_username');
    if (!username) return;
    const normalizedBody = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml) : body;
    const images = Array.from(new Set(hiveService.extractImages(normalizedBody || '')));
    let finalCover = coverImage === '__custom' ? customCover.trim() : coverImage;
    if (finalCover) {
      const coverIdx = images.indexOf(finalCover);
      if (coverIdx > -1) images.splice(coverIdx, 1);
      images.unshift(finalCover);
    }
    const metadata = buildArticleMetadataForTargetApp(targetApp, { tags: [...tags], format: 'markdown' });
    if (bodyFormat === 'shortcode') metadata.shortcode = true;
    const payload = {
      author: username,
      title,
      body: normalizedBody,
      body_html: bodyHtml,
      json_metadata: metadata,
      community,
      tags,
      target_app: targetApp,
      body_format: bodyFormat,
      reward_option: rewardOption,
      beneficiaries: beneficiariesTags,
      cover_image: finalCover || null,
      include_in_blog: includeInBlog,
    };
    try {
      setSavingTemplate(true);
      await workspaceApi.createTemplate(payload);
      // Clean up the autosave draft so we don't leave an orphan behind.
      if (draftIdRef.current) {
        await workspaceApi.deleteDraft(draftIdRef.current, username).catch(() => {});
        draftIdRef.current = null;
        setDraftId(null);
      }
      navigate('/create-article/templates');
    } catch (err) {
      setError(err.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Lock body scroll while modals are open
  useBodyScrollLock(scheduleModalOpen || previewOpen);

  // Check if keychain user already has a stored posting key (run when modal opens)
  useEffect(() => {
    if (!scheduleModalOpen) return;
    if (hiveService.getLoginMethod() !== 'keychain') return;
    const username = user?.username || localStorage.getItem('hive_username');
    if (!username) return;
    setStoredKeyStatus('checking');
    setShowKeyOverride(false);
    setStorePostingKey(false);
    setShowScheduleKeyAdvanced(false);
    userApi.getStoredPostingKeyStatus(username)
      .then(({ hasKey }) => setStoredKeyStatus(hasKey ? 'has-key' : 'no-key'))
      .catch(() => setStoredKeyStatus('no-key')); // fall back to manual entry on error
  }, [scheduleModalOpen, user?.username]);

  // ── Schedule submit ─────────────────────────────────────────────────────────
  const handleScheduleSubmit = async () => {
    if (!scheduleDateTime) return;
    const dt = new Date(scheduleDateTime);
    if (dt <= new Date()) { alert('Please choose a future date and time.'); return; }

    const normalizedBody = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml) : body;
    if (!title.trim()) { alert('Article title is required.'); return; }
    if (!community) { alert('Please select a community.'); return; }
    if (!normalizedBody.trim()) { alert('Article body cannot be empty.'); return; }

    const username = user?.username || localStorage.getItem('hive_username');
    const isKeychainUser = hiveService.getLoginMethod() === 'keychain';

    // Get the posting key to send
    let postingKey;
    const usingStoredKey = isKeychainUser && storedKeyStatus === 'has-key' && !showKeyOverride;
    if (isKeychainUser && !usingStoredKey) {
      if (scheduleKeyStatus !== 'valid') { alert('Please verify your posting key first.'); return; }
      postingKey = schedulePostingKey.trim();
    } else if (!isKeychainUser) {
      postingKey = sessionStorage.getItem('hive_posting_key') || '';
      if (!postingKey) { alert('No posting key found in this session. Please re-login.'); return; }
    }
    // usingStoredKey → postingKey stays undefined; server will use stored key

    const base = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const permlink = base + '-' + Math.random().toString(36).substring(4, 8);

    const tagArray = [...tags];
    const communityTag = community.startsWith('hive-') ? community : `hive-${community}`;
    if (!tagArray.includes(communityTag)) tagArray.unshift(communityTag);
    const metadata = buildArticleMetadataForTargetApp(targetApp, { tags: tagArray, format: 'markdown' });
    if (bodyFormat === 'shortcode') metadata.shortcode = true;

    const images = Array.from(new Set(hiveService.extractImages(normalizedBody || '')));
    let finalCover = coverImage === '__custom' ? customCover.trim() : coverImage;
    if (finalCover) {
      const coverIdx = images.indexOf(finalCover);
      if (coverIdx > -1) images.splice(coverIdx, 1);
      images.unshift(finalCover);
    }
    if (images.length > 0) {
      metadata.image = images;
      metadata.images = images;
    }
    if (finalCover) { metadata.featured_image = finalCover; metadata.thumbnail = finalCover; }

    const beneficiaries = beneficiariesTags
      .map(t => t.split('=').map(s => s.trim()))
      .filter(pair => pair.length === 2 && pair[0] && pair[1])
      .map(([acct, w]) => {
        let num = parseInt(w, 10) || 0;
        if (num <= 100) num = num * 100;
        return { account: acct, weight: num };
      });

    try {
      setScheduling(true);

      // If user chose to store their posting key, do that first
      if (isKeychainUser && !usingStoredKey && storePostingKey && postingKey) {
        try {
          await userApi.storePostingKey(username, postingKey);
          setStoredKeyStatus('has-key');
        } catch (storeErr) {
          console.warn('Failed to store posting key:', storeErr.message);
          // Non-fatal — continue with scheduling
        }
      }

      await workspaceApi.createScheduled({
        author: username,
        permlink,
        title,
        body: normalizedBody,
        body_html: bodyHtml,
        json_metadata: metadata,
        community,
        tags: tagArray,
        target_app: targetApp,
        body_format: bodyFormat,
        reward_option: rewardOption,
        beneficiaries,
        cover_image: finalCover || null,
        include_in_blog: includeInBlog,
        scheduled_at: dt.toISOString(),
        ...(postingKey ? { posting_key: postingKey } : {}),
      });
      // Delete draft if it exists
      if (draftIdRef.current) {
        await workspaceApi.deleteDraft(draftIdRef.current, username).catch(() => {});
        draftIdRef.current = null;
      }
      setScheduleModalOpen(false);
      navigate('/create-article/scheduled');
    } catch (err) {
      alert('Failed to schedule post: ' + (err.message || 'Unknown error'));
    } finally {
      setScheduling(false);
    }
  };

  if (!isAuthenticated) return <Navigate to="/" replace />;

  if (loadingWorkspace) {
    return (
      <>
        <SidebarHeader title={isEditMode ? 'Edit Article' : 'Create Article'} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
        <div className="create-article-page">
          <p style={{ padding: '32px 0', color: 'var(--text-secondary)' }}>Loading…</p>
        </div>
      </>
    );
  }

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    const normalizedBody = bodyHtml && bodyHtml.trim() ? htmlToMarkdown(bodyHtml) : body;

    if (!title.trim()) return setError('Article title is required');
    if (!community) return setError('Please select a community');
    if (!normalizedBody.trim()) return setError('Article body cannot be empty');
    if (coverImage === '__custom') {
      if (!customCover.trim()) return setError('Custom cover image URL is required');
      if (customCoverStatus !== 'valid') {
        return setError('Custom cover image URL must load successfully before posting');
      }
    }

    // ── Edit mode: broadcast update to Hive, no DB draft ──────────────────
    if (isEditMode) {
      if (editWindowExpired) {
        setError('This article can no longer be edited after 7 days.');
        return;
      }
      try {
        setPosting(true);
        postingRef.current = true;
        const username = user?.username || localStorage.getItem('hive_username');

        const tagArray = [];
        const communityForTag = community.startsWith('hive-') ? community : `hive-${community}`;
        tagArray.push(communityForTag);
        if (lockedFirstTag) tagArray.push(lockedFirstTag);
        tagArray.push(...tags);

        const metadata = buildArticleMetadataForTargetApp(targetApp, { tags: tagArray, format: 'markdown' });
        if (bodyFormat === 'shortcode') metadata.shortcode = true;

        const images = Array.from(new Set(hiveService.extractImages(normalizedBody || '')));
        let finalCover = coverImage === '__custom' ? customCover.trim() : coverImage;
        if (finalCover) {
          const coverIdx = images.indexOf(finalCover);
          if (coverIdx > -1) images.splice(coverIdx, 1);
          images.unshift(finalCover);
        }
        if (images.length > 0) {
          metadata.image = images;
          metadata.images = images;
        }
        if (finalCover) { metadata.featured_image = finalCover; metadata.thumbnail = finalCover; }

        const mentionMatches = Array.from(
          (normalizedBody || '').matchAll(/@([a-z][a-z0-9.-]{2,15})/gi),
          m => m[1].toLowerCase()
        );
        metadata.users = Array.from(new Set(mentionMatches));

        const broadcastBody = bodyFormat === 'markdown' ? shortcodesToMarkdown(normalizedBody) : normalizedBody;

        await hiveService.editPost(
          username,
          editOriginalPermlink,
          title,
          broadcastBody,
          '',
          editOriginalParentPermlink,
          metadata
        );

        alert('Article updated successfully');
        navigate(buildPostPath(username, editOriginalPermlink));
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to update article');
      } finally {
        setPosting(false);
        postingRef.current = false;
      }
      return;
    }
    // ── End edit mode ──────────────────────────────────────────────────────

    // generate permlink once, ahead of time
    // sanitize title: spaces -> dash, remove/replace special chars
    const base = title
      .toLowerCase()
      .replace(/\s+/g, '-')               // spaces to dashes
      .replace(/[^a-z0-9-]/g, '-')         // non-alphanumeric to dash
      .replace(/-+/g, '-')                 // collapse multiple dashes
      .replace(/^-|-$/g, '');              // trim leading/trailing dashes
    const permlink = base + '-' + Math.random().toString(36).substring(4, 8);
    const username = user?.username || localStorage.getItem('hive_username');

    // parse beneficiaries tags into objects
    const beneficiaries = beneficiariesTags
      .map(t => t.split('=').map(s => s.trim()))
      .filter(pair => pair.length === 2 && pair[0] && pair[1])
      .map(([acct, w]) => {
        let num = parseInt(w, 10) || 0;
        // treat values <=100 as percent and convert to hundredths
        if (num <= 100) num = num * 100;
        return { account: acct, weight: num };
      });

    try {
      setPosting(true);
      postingRef.current = true;
      const tagArray = [...tags];
      const communityTag = community.startsWith('hive-') ? community : `hive-${community}`;
      if (!tagArray.includes(communityTag)) tagArray.unshift(communityTag);
      const metadata = buildArticleMetadataForTargetApp(targetApp, {
        tags: tagArray,
        format: 'markdown'
      });
      // Signal shortcode body so our app knows how to render it;
      // other frontends only see format: 'markdown' and ignore this field.
      if (bodyFormat === 'shortcode') {
        metadata.shortcode = true;
      }
      // detect images from body as well (should already be in state)
      const images = [...detectedImages];
      let finalCover = coverImage;
      if (coverImage === '__custom') {
        finalCover = customCover.trim();
      }
      if (finalCover) {
        // ensure cover is included and first
        const coverIdx = images.indexOf(finalCover);
        if (coverIdx > -1) images.splice(coverIdx, 1);
        images.unshift(finalCover);
      }
      if (images.length > 0) {
        metadata.image = images;
        metadata.images = images;
      }
      if (finalCover) {
        // Normalize commonly used featured-image keys across Hive frontends/apps.
        metadata.featured_image = finalCover;
        metadata.thumbnail = finalCover;
      }

      // Extract @mentions from the body and populate the users field
      const mentionMatches = Array.from(
        (normalizedBody || '').matchAll(/@([a-z][a-z0-9.-]{2,15})/gi),
        m => m[1].toLowerCase()
      );
      metadata.users = Array.from(new Set(mentionMatches));

      // Convert shortcodes to standard markdown if the user chose markdown format
      const broadcastBody = bodyFormat === 'markdown' ? shortcodesToMarkdown(normalizedBody) : normalizedBody;

      // insert draft row into our database
      await postsApi.createDraft({
        type: 'long',
        author: username,
        permlink,
        title,
        body: normalizedBody,
        json_metadata: metadata,
        reward_option: rewardOption,
        beneficiaries,
        cover_image: finalCover || null,
        include_in_blog: includeInBlog
      });

      // broadcast to Hive using the same permlink (pass reward selection + beneficiaries)
      await hiveService.createArticle(title, broadcastBody, metadata, permlink, rewardOption, beneficiaries);
      alert('Article published');

      // update status to posted (clears pending flag)
      await postsApi.updateStatus({ type: 'long', author: username, permlink, is_pending: false });

      // delete workspace draft if it was loaded from one
      if (draftIdRef.current) {
        await workspaceApi.deleteDraft(draftIdRef.current, username).catch(() => {});
        draftIdRef.current = null;
        setDraftId(null);
      }

      // navigate to the article page if permlink available
      if (username && permlink) {
        navigate(buildPostPath(username, permlink));
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to publish article');
      // if we generated a permlink and have username, mark the record as error
      try {
        if (username) {
          await postsApi.updateStatus({ type: 'long', author: username, permlink, is_pending: true, pending_reason: 'error' });
        }
      } catch (_) {
        // ignore
      }
    } finally {
      setPosting(false);
      postingRef.current = false;
      setPreviewOpen(false);
    }
  };

  // Handle rich editor content change
  const handleBodyChange = (html) => {
    isDirtyRef.current = true;
    setBodyHtml(html);
    const markdown = htmlToMarkdown(html, bodyFormat);
    setBody(markdown);
  };

  return (
    <>
      <SidebarHeader title={isEditMode ? 'Edit Article' : 'Create Article'} onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="create-article-page">
      <div className="page-header">
        <h1>{isEditMode ? 'Edit Article' : 'Write an Article'}</h1>
        <p className="subtitle">{isEditMode ? 'Update your article. Community and first tag are locked.' : 'Long-form posts (Markdown supported). Choose a community and tags.'}</p>
        <button 
          className="distraction-less-toggle desktop-only" 
          onClick={() => {
            setIsDistractionlessMode(m => !m);
            document.body.classList.toggle('distraction-less-reading');
          }}
          title={isDistractionlessMode ? 'Exit distraction-less mode' : 'Enter distraction-less mode'}
        >
          {isDistractionlessMode ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="9" />
              <rect x="14" y="3" width="7" height="5" />
              <rect x="14" y="12" width="7" height="9" />
              <rect x="3" y="16" width="7" height="5" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>
      </div>

      <form className="post-form article-page-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            className="title-input"
            placeholder="Article title..."
            value={title}
            onChange={(e) => { isDirtyRef.current = true; setTitle(e.target.value); }}
            maxLength={255}
          />
        </div>

        <div className="form-group">
          <div className="md-toolbar">
            <button type="button" onClick={() => richEditorRef.current?.focus()} title="Focus editor" className="editor-info-btn" style={{ cursor: 'default', background: 'transparent', border: 'none', color: 'var(--text-secondary)', marginRight: 'auto' }}>📝 Rich formatting enabled</button>
            <span className="md-charcount">{body.length} chars</span>
          </div>

          <RichEditor
            ref={richEditorRef}
            value={bodyHtml}
            onChange={handleBodyChange}
            onSubmit={handleSubmit}
            placeholder="Write your article... (Markdown & HTML supported)"
            maxLength={null}
            toolbar={true}
            defaultMode={bodyFormat === 'markdown' ? 'text' : 'editor'}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Community *</label>
          {isEditMode ? (
            <div className="locked-field-display">
              <span>{editCommunityTitle || community}</span>
              <span className="locked-badge">Locked</span>
            </div>
          ) : (
            <CommunitySelect
              communities={communities}
              value={community}
              onChange={setCommunity}
              placeholder="Select a community..."
              loading={communitiesLoading}
              displayTitle={urlCommunityTitle}
            />
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Tags (comma, spacebar or return/enter to add)</label>
          {isEditMode && lockedFirstTag && (
            <div className="locked-tags-row">
              <span className="locked-tag-chip">
                {lockedFirstTag}
                <span className="locked-badge">Locked</span>
              </span>
            </div>
          )}
          <TagInput tags={tags} onChange={setTags} placeholder="e.g. technology, tutorial" />
        </div>

        <button
          type="button"
          className="secondary-btn advanced-btn"
          onClick={() => setShowAdvanced(v => !v)}
          style={{ marginBottom: 12 }}
        >
          {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
        </button>

        {showAdvanced && (
          <>
            <div className="form-group">
              <label className="form-label">Cover image</label>
              <div className="cover-image-picker">
                <button
                  type="button"
                  className={`cover-choice-btn ${coverImage === '' ? 'selected' : ''}`}
                  onClick={() => {
                    setCoverImage('');
                    setCustomCover('');
                  }}
                >
                  No cover image
                </button>
                <button
                  type="button"
                  className={`cover-choice-btn ${coverImage === '__custom' ? 'selected' : ''}`}
                  onClick={() => setCoverImage('__custom')}
                >
                  Custom image URL
                </button>
              </div>
              {coverImage === '__custom' && (
                <div className="custom-cover-input-wrap">
                  <input
                    type="text"
                    className="title-input"
                    placeholder="https://..."
                    value={customCover}
                    onChange={e => setCustomCover(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                  {customCoverStatus === 'loading' && <div className="cover-status cover-status-loading">Checking image URL...</div>}
                  {customCoverStatus === 'valid' && <div className="cover-status cover-status-valid">Image URL is valid.</div>}
                  {customCoverStatus === 'invalid' && <div className="cover-status cover-status-invalid">Could not load image from this URL.</div>}
                </div>
              )}
              {detectedImages.length > 0 && (
                <div className="cover-thumbnails-grid">
                  {detectedImages.map((url, i) => (
                    <button
                      key={`${url}-${i}`}
                      type="button"
                      className={`cover-thumb-btn ${coverImage === url ? 'selected' : ''}`}
                      onClick={() => {
                        setCoverImage(url);
                        setCustomCover('');
                      }}
                    >
                      <img src={url} alt={`Detected cover option ${i + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group include-blog-group">
              <label className="form-label">
                <span>Include in my blog</span>
                <input
                  type="checkbox"
                  checked={includeInBlog}
                  onChange={e=>setIncludeInBlog(e.target.checked)}
                />
              </label>
            </div>

            {!isEditMode && (
              <>
                <div className="form-group">
                  <label className="form-label">Reward distribution</label>
                  <select
                    className="tags-input"
                    value={rewardOption}
                    onChange={e => setRewardOption(e.target.value)}
                  >
                    <option value="50">50% HBD and 50% HP</option>
                    <option value="100">100% Hive Power</option>
                    <option value="-3">Use @reward.app</option>
                    <option value="-4">Use @commentrewarder</option>
                    <option value="-1">Burn</option>
                    <option value="-2">Donate to Hive Fund</option>
                    <option value="0">Decline</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Beneficiaries</label>
                  <button type="button" className="secondary-btn blwh-btn" onClick={() => setShowBeneficiariesModal(true)}>
                    Edit beneficiaries
                  </button>
                  {beneficiariesTags.length > 0 && (
                    <div className="benef-summary">
                      {beneficiariesTags.map(t => {
                        const [a,w] = t.split('=');
                        return `${a}=${w}%`;
                      }).join(', ')}
                    </div>
                  )}
                </div>
                <BeneficiariesModal
                  isOpen={showBeneficiariesModal}
                  onClose={() => setShowBeneficiariesModal(false)}
                  initialTags={beneficiariesTags}
                  onSave={setBeneficiariesTags}
                />
              </>
            )}

            {targetAppOptions.length > 0 && (
              <div className="form-group">
                <label className="form-label">Target app</label>
                {isEditMode ? (
                  <div className="locked-field-display">
                    <span>{targetAppOptions.find(a => a.key === targetApp)?.label || targetApp}</span>
                    <span className="locked-badge">Locked</span>
                  </div>
                ) : (
                  <select
                    className="tags-input"
                    value={targetApp}
                    onChange={e => setTargetApp(e.target.value)}
                  >
                    {targetAppOptions.map(app => (
                      <option key={app.key} value={app.key}>{app.label}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Body format</label>
              <select
                className="tags-input"
                value={bodyFormat}
                onChange={e => setBodyFormat(e.target.value)}
              >
                <option value="markdown">Markdown — compatible with all Hive frontends</option>
                <option value="shortcode">Shortcode — only rendered correctly by this app</option>
              </select>
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* Autosave status indicator */}
        {!isEditMode && autosaveStatus !== 'idle' && (
          <div className={`autosave-status autosave-status--${autosaveStatus}`}>
            {autosaveStatus === 'saving' && '⏳ Saving draft…'}
            {autosaveStatus === 'saved'  && '✓ Draft saved'}
            {autosaveStatus === 'error'  && '⚠ Autosave failed'}
          </div>
        )}

        <div className="form-actions">
          {!isEditMode && (
            <div className="form-actions-row">
              <button type="button" className="secondary-btn preview-form-btn" onClick={() => setPreviewOpen(true)}>Preview</button>
              <button type="button" className="action-draft-btn" onClick={() => { isDirtyRef.current = true; saveCurrentState(); }} disabled={autosaveStatus === 'saving'} title="Save draft now">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                {draftId ? 'Update Draft' : 'Save Draft'}
              </button>
              <button type="button" className="action-template-btn" onClick={handleSaveAsTemplate} disabled={savingTemplate || posting} title="Save current content as a reusable template">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                {savingTemplate ? 'Saving…' : 'Save as Template'}
              </button>
              <button type="button" className="action-schedule-btn" onClick={() => { setScheduleModalOpen(true); setScheduleKeyStatus('idle'); setSchedulePostingKey(''); setStoredKeyStatus(null); setShowKeyOverride(false); setStorePostingKey(false); setShowScheduleKeyAdvanced(false); }} disabled={posting}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Schedule
              </button>
            </div>
          )}
          {isEditMode && (
            <button type="button" className="secondary-btn preview-form-btn" onClick={() => setPreviewOpen(true)}>Preview</button>
          )}
          <div style={{ flex: 1 }} className="form-actions-spacer" />
          <div className="form-actions-row">
            <button type="button" className="cancel-form-btn" onClick={() => navigate(-1)}>Cancel</button>
            <button type="submit" className="submit-form-btn" disabled={posting || (isEditMode && editWindowExpired)}>
              {isEditMode
                ? (posting ? 'Updating...' : 'Update Article')
                : (posting ? 'Publishing...' : 'Publish Article')}
            </button>
          </div>
        </div>
      </form>

      {/* Schedule Modal */}
      {scheduleModalOpen && (
        <div className="preview-backdrop" onClick={() => setScheduleModalOpen(false)}>
          <div className="preview-modal schedule-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="schedule-modal-header">
              <h2>Schedule Article</h2>
              <button className="preview-close-btn" onClick={() => setScheduleModalOpen(false)}>×</button>
            </div>
            <div className="preview-content">

              <div className="form-group">
                <label className="form-label">Publish at</label>
                <input
                  type="datetime-local"
                  className="title-input"
                  value={scheduleDateTime}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  onChange={e => setScheduleDateTime(e.target.value)}
                />
              </div>

              {hiveService.getLoginMethod() === 'keychain' && (
                <>
                  {/* Stored key — show notice and option to override */}
                  {storedKeyStatus === 'has-key' && !showKeyOverride && (
                    <div className="form-group">
                      <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '0 0 6px' }}>
                        ✓ Your stored posting key will be used to broadcast this post.
                      </p>
                      <button type="button" className="secondary-btn" style={{ fontSize: '0.8rem' }}
                        onClick={() => { setShowKeyOverride(true); setScheduleKeyStatus('idle'); setSchedulePostingKey(''); }}>
                        Use a different key
                      </button>
                    </div>
                  )}

                  {/* Manual key entry — either no stored key, or user chose override */}
                  {(storedKeyStatus === 'no-key' || showKeyOverride || storedKeyStatus === 'checking') && (
                    <div className="form-group">
                      <label className="form-label">Posting key required</label>
                      <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '4px 0 10px' }}>
                        Because you use Hive Keychain, the scheduler needs your private posting key to broadcast this post on the scheduled date and time. It will be stored <strong>AES-256 encrypted</strong> and never accessible in plaintext.
                      </p>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="password"
                          className="title-input"
                          placeholder="5K… posting key"
                          value={schedulePostingKey}
                          onChange={e => { setSchedulePostingKey(e.target.value); setScheduleKeyStatus('idle'); }}
                          style={{ flex: 1 }}
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={handleVerifyScheduleKey}
                          disabled={scheduleKeyStatus === 'verifying' || !schedulePostingKey.trim()}
                        >
                          {scheduleKeyStatus === 'verifying' ? 'Checking…' : 'Verify'}
                        </button>
                      </div>
                      {scheduleKeyStatus === 'valid'   && <div className="cover-status cover-status-valid"   style={{ marginTop: 6 }}>✓ Key verified</div>}
                      {scheduleKeyStatus === 'invalid' && <div className="cover-status cover-status-invalid" style={{ marginTop: 6 }}>✗ Invalid posting key</div>}

                      {/* Advanced: remember this key */}
                      {storedKeyStatus === 'no-key' && !showKeyOverride && (
                        <div style={{ marginTop: 12 }}>
                          <button type="button"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-secondary)', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={() => setShowScheduleKeyAdvanced(v => !v)}>
                            {showScheduleKeyAdvanced ? '▾' : '▸'} Advanced
                          </button>
                          {showScheduleKeyAdvanced && (
                            <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 6 }}>
                              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                                <input type="checkbox" checked={storePostingKey}
                                  onChange={e => setStorePostingKey(e.target.checked)}
                                  style={{ marginTop: 2, flexShrink: 0 }} />
                                <span>Remember this key for future scheduled posts</span>
                              </label>
                              {storePostingKey && (
                                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                                  Your key will be stored encrypted on the server. You can remove it at any time from <strong>Settings → Data Sync</strong>.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {hiveService.getLoginMethod() !== 'keychain' && (
                <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', margin: '4px 0 12px' }}>
                  Your posting key will be stored <strong>AES-256 encrypted</strong> and used only to broadcast this post at the scheduled time.
                </p>
              )}

              <div className="preview-actions">
                <button className="secondary-btn" onClick={() => setScheduleModalOpen(false)}>Cancel</button>
                <button
                  className="submit-form-btn"
                  onClick={handleScheduleSubmit}
                  disabled={
                    scheduling || !scheduleDateTime ||
                    (hiveService.getLoginMethod() === 'keychain' &&
                      storedKeyStatus !== 'has-key' && scheduleKeyStatus !== 'valid') ||
                    (hiveService.getLoginMethod() === 'keychain' &&
                      storedKeyStatus === 'has-key' && showKeyOverride && scheduleKeyStatus !== 'valid')
                  }
                >
                  {scheduling ? 'Scheduling…' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="preview-backdrop" onClick={() => setPreviewOpen(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="preview-close-btn" onClick={() => setPreviewOpen(false)}>×</button>
            <div className="preview-content">
              <div className="post-preview-header">
                <h1>{title || 'Untitled'}</h1>
                <div className="preview-meta">
                  c/{community || 'flexus'} • {tags.join(', ')} • {rewardOption === '50' ? '50/50' : rewardOption === '100' ? '100% HP' : rewardOption === '0' ? 'Decline' : rewardOption === '-1' ? 'Burn' : rewardOption === '-2' ? 'Hive Fund' : rewardOption === '-3' ? 'reward.app' : rewardOption === '-4' ? 'commentrewarder' : rewardOption}
                  {targetAppOptions.length > 0 && (
                    <> • app: {targetAppOptions.find(a => a.key === targetApp)?.label || targetApp}</>
                  )}
                  {beneficiariesTags.length > 0 && (
                    <> • beneficiaries: {beneficiariesTags.map(t=>{const [a,w]=t.split('=');return `${a}=${w}%`;}).join(', ')}</>
                  )}
                </div>
              </div>
              <div className="preview-body">
                <MarkdownRenderer content={body || ''} />
              </div>
              <div className="preview-actions">
                <button className="secondary-btn action-close-preview-btn" onClick={() => setPreviewOpen(false)}>Keep writing</button>
                <button className="submit-form-btn" onClick={handleSubmit} disabled={posting}>{posting ? (isEditMode ? 'Updating...' : 'Publishing...') : (isEditMode ? 'Update' : 'Post')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CreateArticle;
